'use client';

import { Button } from '@shared/components/shadcn';
import { getSiteConfig } from '@shared/config';
import { Verification } from '@shared/icons/system';
import { useTranslations } from 'next-intl';
import { useFormContext } from 'react-hook-form';
import { useMultistepForm } from '../../../form-controls/MultistepForm/hooks/useMultistepForm';
import { useKycDialog } from '../../../global-dialogs/components/KycDialog/context/KycDialogContext';
import type { VerificationInfoStepProps } from '../types';

function VerificationInfoStep({ firstName, onSkipVerification }: VerificationInfoStepProps) {
  const t = useTranslations('my_account.profile.profile_info');
  const tForm = useTranslations('form');
  const { goToNextStep } = useMultistepForm();
  const { watch } = useFormContext();
  const { openKycDialog } = useKycDialog();

  const { isShowDetailedNameChangeVerification, nameChangeKycVerificationMethods } = getSiteConfig().PROFILE_INFO;

  const watchedFirstName = watch('firstName');

  const handleContinue = () => {
    openKycDialog({
      verificationMethods: nameChangeKycVerificationMethods,
      onComplete: async () => {
        goToNextStep();
      },
      onSkipVerification,
      dataTrackFeature: 'My Account',
      dataTrackPrivacy: 'Exclude',
    });
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 p-6 h-full gap-6" data-track-container="Verify Name">
      <Verification size="extra-extra-large" data-testid="verification-icon" />

      {/* Verification message */}
      <div className="space-y-2">
        <h4>{t.rich('name_edit.verification_title', { name: () => <span data-track-privacy="Mask">{watchedFirstName || firstName}</span> })}</h4>
        {isShowDetailedNameChangeVerification ? (
          <div className="space-y-4 paragraph-explanatory text-muted-foreground">
            <p>{t('name_edit.verification_description_intro')}</p>
            <p>{t('name_edit.verification_upload_intro')}</p>
            <ul className="list-disc space-y-1 pl-5">
              <li>{t('name_edit.verification_document_birth_certificate')}</li>
              <li>{t('name_edit.verification_document_marriage_certificate')}</li>
              <li>{t('name_edit.verification_document_divorce_certificate')}</li>
              <li>{t('name_edit.verification_document_change_of_name_certificate')}</li>
            </ul>
            <p>{t('name_edit.verification_photo_guidance')}</p>
          </div>
        ) : (
          <p className="paragraph-explanatory text-muted-foreground">{t('name_edit.verification_description')}</p>
        )}
      </div>

      <div className="mt-auto shrink-0" data-track-container="Actions">
        <Button onClick={handleContinue} className="w-full" data-track-name="Continue">
          {tForm('buttons.continue')}
        </Button>
      </div>
    </div>
  );
}

export { VerificationInfoStep };
